import type {
  JunctionAttachOptions,
  SmrtCollectionOptions,
} from '@happyvertical/smrt-core';
import { SmrtJunction, smrt } from '@happyvertical/smrt-core';
import { ContentReference } from './content-reference';

export interface ContentReferencesOptions extends SmrtCollectionOptions {}

/**
 * The `attach()` override below restores find-or-create idempotency for
 * `(sourceId, targetId)` — duplicate calls return the existing row
 * unchanged, preserving `id` and `createdAt`. This matters because
 * `ContentReference` rows are externally addressable via
 * `/api/v1/contentreferences/[id]`.
 *
 * Two REST entry points (both auto-generated by the scanner):
 * - `POST /api/v1/contentreferences` (from model CRUD) calls
 *   `collection.create()` which is upsert-based — id/createdAt get
 *   rewritten on conflict. Convenient for callers that don't care
 *   about row id stability.
 * - `POST /api/v1/contentreferences/attach` (from the override below)
 *   is idempotent. Use this for stable URLs.
 *
 * Internal callers (`Content.addReference()`) always hit the idempotent
 * path because they call the collection directly.
 *
 * The `/attach` route exists because R2 round-7 added `@smrt()` to this
 * class, which made the scanner pick up the override as a custom
 * collection method route. Pre-R2 had a `/link` route from the
 * pre-rename method name.
 */
// Decorator with empty config — only needed so the scanner detects the
// class. See FactContentCollection for the full rationale.
@smrt()
export class ContentReferences extends SmrtJunction<ContentReference> {
  static readonly _itemClass = ContentReference;
  protected leftField = 'sourceId';
  protected rightField = 'targetId';
  // content_references has no sort_order column — preserve insertion order
  // by sorting on created_at, and disable setLinks position auto-indexing
  // so it doesn't try to write integer indices into the timestamp column.
  protected sortField: string | null = 'createdAt';
  protected positionField: string | null = null;

  async getForSource(sourceId: string): Promise<ContentReference[]> {
    return (await this.list({
      where: { sourceId },
      orderBy: 'created_at ASC',
    })) as ContentReference[];
  }

  async getForTarget(targetId: string): Promise<ContentReference[]> {
    return (await this.list({
      where: { targetId },
      orderBy: 'created_at ASC',
    })) as ContentReference[];
  }

  /**
   * Find-or-create idempotency: if a reference already exists for
   * (sourceId, targetId), return the existing row unchanged instead of
   * upserting a new row. This preserves the existing row's `id` and
   * `createdAt`, which is important because reference rows are
   * externally addressable via `/api/v1/contentreferences/[id]`.
   *
   * The base `SmrtJunction.attach` flow (this.create → db.upsert) would
   * overwrite both columns on every duplicate call.
   *
   * Reference pinning (main): `opts.targetVersion` pins the citation to a
   * specific `ContentVersion.version` for drift detection. Re-attaching an
   * existing edge with a different `targetVersion` updates the pin in place;
   * `undefined` leaves an existing pin untouched, while a brand-new row
   * defaults the pin to `null` (unpinned).
   */
  async attach(
    sourceId: string,
    targetId: string,
    opts: JunctionAttachOptions = {},
  ): Promise<ContentReference> {
    const targetVersion = opts.targetVersion as number | null | undefined;
    const existing = (await this.get({
      sourceId,
      targetId,
    })) as ContentReference | null;
    if (existing) {
      if (
        targetVersion !== undefined &&
        existing.targetVersion !== targetVersion
      ) {
        existing.targetVersion = targetVersion;
        await existing.save();
      }
      return existing;
    }
    return super.attach(sourceId, targetId, {
      ...opts,
      targetVersion: targetVersion ?? null,
    });
  }

  async unlink(sourceId: string, targetId: string): Promise<void> {
    await this.detach(sourceId, targetId);
  }
}
